import {on} from 'delegated-events'
import {SELECTORS} from './types'
import {getDialogState, resetDialogState as resetState} from './state'
import {createServersEmptyState, createServerRow, createAllowlistRow, createAllowlistTable} from './templates'

// Hardcoded demo values for hidden UI
const DEMO_AVATAR_URL = 'https://avatars.githubusercontent.com/u/1?v=4'
const DEMO_USER_NAME = 'octocat'

function showSuccessBanner(): void {
  const banner = document.getElementById('allowlist-success-banner')
  if (banner) {
    banner.hidden = false
    banner.scrollIntoView({behavior: 'smooth', block: 'start'})
  }
}

function getDialogController(element: Element): Element | null {
  // First try to find the controller as an ancestor
  const controllerFromAncestor = element.closest('[data-controller="allowlist-dialog"]')
  if (controllerFromAncestor) return controllerFromAncestor

  // If not found, the element might be in the header/footer (outside the controller div)
  // In that case, find the dialog first, then find the controller within it
  const dialog = element.closest('#create-allowlist-dialog') || element.closest('[id^="edit-allowlist-dialog-"]')
  if (dialog) {
    return dialog.querySelector('[data-controller="allowlist-dialog"]')
  }
  return null
}

function getDialog(element: Element): Element | null {
  // Support both create and edit dialogs
  return element.closest('[id$="-allowlist-dialog"], [id^="edit-allowlist-dialog-"]')
}

function showPage(controller: Element, page: 'main' | 'add-server'): void {
  const mainPage = controller.querySelector<HTMLElement>('[data-allowlist-dialog-target="mainPage"]')
  const addServerPage = controller.querySelector<HTMLElement>('[data-allowlist-dialog-target="addServerPage"]')

  // Get the dialog to find header/footer elements
  const dialog = getDialog(controller)

  // Header elements
  const backButton = dialog?.querySelector<HTMLElement>('[data-allowlist-dialog-target="backButton"]')
  const dialogTitle = dialog?.querySelector('[data-allowlist-dialog-target="dialogTitle"]')

  // Footer elements
  const mainFooter = dialog?.querySelector<HTMLElement>('[data-allowlist-dialog-target="mainFooter"]')
  const addServerFooter = dialog?.querySelector<HTMLElement>('[data-allowlist-dialog-target="addServerFooter"]')

  if (page === 'main') {
    // Show main page content
    if (mainPage) mainPage.hidden = false
    if (addServerPage) addServerPage.hidden = true

    // Update header - hide back button, restore original title
    if (backButton) backButton.hidden = true
    if (dialogTitle) {
      const originalTitle = dialogTitle.getAttribute('data-original-title') || dialogTitle.textContent || ''
      dialogTitle.textContent = originalTitle
    }

    // Update footer - show main footer, hide add-server footer
    if (mainFooter) mainFooter.hidden = false
    if (addServerFooter) addServerFooter.hidden = true

    // Reset selection when going back
    resetServerSelection(controller)
  } else {
    // Show add-server page content
    if (mainPage) mainPage.hidden = true
    if (addServerPage) addServerPage.hidden = false

    // Update header - show back button, save original title and change to Add server
    if (backButton) backButton.hidden = false
    if (dialogTitle) {
      if (!dialogTitle.getAttribute('data-original-title')) {
        dialogTitle.setAttribute('data-original-title', dialogTitle.textContent || '')
      }
      // eslint-disable-next-line i18n-text/no-en
      dialogTitle.textContent = 'Add server'
    }

    // Update footer - hide main footer, show add-server footer
    if (mainFooter) mainFooter.hidden = true
    if (addServerFooter) addServerFooter.hidden = false
  }
}

function resetServerSelection(controller: Element): void {
  const state = getDialogState()
  state.setCurrentSelectedServer(null)

  const selectedText = controller.querySelector('[data-allowlist-dialog-target="selectedServerText"]')
  if (selectedText) {
    // eslint-disable-next-line i18n-text/no-en
    selectedText.textContent = 'Select server name'
    selectedText.classList.add('color-fg-muted')
  }

  // Clear hidden inputs
  const idInput = controller.querySelector<HTMLInputElement>('[data-allowlist-dialog-target="selectedServerId"]')
  const nameInput = controller.querySelector<HTMLInputElement>('[data-allowlist-dialog-target="selectedServerName"]')
  const urlInput = controller.querySelector<HTMLInputElement>('[data-allowlist-dialog-target="selectedServerUrl"]')
  if (idInput) idInput.value = ''
  if (nameInput) nameInput.value = ''
  if (urlInput) urlInput.value = ''
}

function updateServersList(controller: Element): void {
  const state = getDialogState()
  const serversList = controller.querySelector('[data-test-selector="create-allowlist-servers-list"]')
  if (!serversList) return

  // Find the header
  const header = serversList.querySelector('.Box-header')
  const existingRows = serversList.querySelectorAll('.Box-row')

  // Remove existing rows
  for (const row of existingRows) {
    row.remove()
  }

  if (state.addedServers.length === 0) {
    // Show empty state
    const emptyState = createServersEmptyState()
    header?.after(emptyState)
  } else {
    // Add rows for each server (in reverse order so they appear correctly after header)
    const servers = [...state.addedServers]
    for (let i = servers.length - 1; i >= 0; i--) {
      const server = servers[i]!
      const row = createServerRow(server, i)
      header?.after(row)
    }
  }

  // Update server count in header if needed
  const headerText = header?.querySelector('span')
  if (headerText && state.addedServers.length > 0) {
    // eslint-disable-next-line i18n-text/no-en
    headerText.textContent = `Servers (${state.addedServers.length})`
  } else if (headerText) {
    headerText.textContent = 'Servers'
  }
}

function resetDialogFormState(): void {
  resetState()

  const dialog = document.getElementById('create-allowlist-dialog')
  if (!dialog) return

  const controller = dialog.querySelector(SELECTORS.CONTROLLER)
  if (!controller) return

  showPage(controller, 'main')

  // Reset org button text
  const orgSelectText = controller.querySelector('[data-allowlist-dialog-target="orgSelectText"]')
  if (orgSelectText) {
    // eslint-disable-next-line i18n-text/no-en
    orgSelectText.textContent = 'All Organizations'
  }

  // Clear hidden inputs
  const orgHiddenInputs = controller.querySelector('[data-allowlist-dialog-target="orgHiddenInputs"]')
  if (orgHiddenInputs) {
    orgHiddenInputs.textContent = ''
  }

  // Reset organization selections in the SelectPanel
  const orgSelectPanel = dialog.querySelector('#allowlist-org-select-panel')
  if (orgSelectPanel) {
    const selectedItems = orgSelectPanel.querySelectorAll('[aria-selected="true"]')
    for (const item of selectedItems) {
      item.setAttribute('aria-selected', 'false')
    }
  }

  // Also reset form fields
  const nameInput = dialog.querySelector<HTMLInputElement>('input[name="allowlist[name]"]')
  const descInput = dialog.querySelector<HTMLTextAreaElement>('textarea[name="allowlist[description]"]')
  if (nameInput) nameInput.value = ''
  if (descInput) descInput.value = ''

  // Reset the servers list UI
  updateServersList(controller)
}

// ============================================================================
// Event Handlers - registered at module load time (top-level scope)
// ============================================================================

// Show add server page
on('click', '[data-action="click->allowlist-dialog#showAddServerPage"]', function (event) {
  event.preventDefault()
  const controller = getDialogController(event.currentTarget as Element)
  if (controller) {
    showPage(controller, 'add-server')
  }
})

// Show main page (back button or cancel on add server page)
on('click', '[data-action="click->allowlist-dialog#showMainPage"]', function (event) {
  event.preventDefault()
  const controller = getDialogController(event.currentTarget as Element)
  if (controller) {
    showPage(controller, 'main')
  }
})

// Handle server selection from SelectPanel
on('click', '[data-action="click->allowlist-dialog#selectServer"]', function (event) {
  const target = event.currentTarget as HTMLElement
  const controller = getDialogController(target)
  if (!controller) return

  const state = getDialogState()
  const serverId = target.getAttribute('data-value') || ''
  const serverName = target.getAttribute('data-server-name') || ''
  const serverUrl = target.getAttribute('data-server-url') || ''

  state.setCurrentSelectedServer({id: serverId, name: serverName, url: serverUrl})

  // Update display text
  const selectedText = controller.querySelector('[data-allowlist-dialog-target="selectedServerText"]')
  if (selectedText) {
    selectedText.textContent = serverName
    selectedText.classList.remove('color-fg-muted')
  }

  // Update hidden inputs
  const idInput = controller.querySelector<HTMLInputElement>('[data-allowlist-dialog-target="selectedServerId"]')
  const nameInput = controller.querySelector<HTMLInputElement>('[data-allowlist-dialog-target="selectedServerName"]')
  const urlInput = controller.querySelector<HTMLInputElement>('[data-allowlist-dialog-target="selectedServerUrl"]')
  if (idInput) idInput.value = serverId
  if (nameInput) nameInput.value = serverName
  if (urlInput) urlInput.value = serverUrl
})

// Add selected server to allowlist
on('click', '[data-action="click->allowlist-dialog#addSelectedServer"]', function (event) {
  event.preventDefault()
  const controller = getDialogController(event.currentTarget as Element)
  const state = getDialogState()
  if (!controller || !state.currentSelectedServer) return

  state.addServer(state.currentSelectedServer)
  updateServersList(controller)

  // Go back to main page
  showPage(controller, 'main')
})

// Remove server from allowlist
on('click', '.js-remove-server-from-allowlist', function (event) {
  event.preventDefault()
  const target = event.currentTarget as HTMLElement
  const index = parseInt(target.getAttribute('data-server-index') || '-1', 10)

  const state = getDialogState()
  if (state.removeServerAtIndex(index)) {
    const controller = getDialogController(target)
    if (controller) {
      updateServersList(controller)
    }
  }
})

// Save allowlist and add to table
on('click', '[data-action="click->allowlist-dialog#saveAllowlist"]', function (event) {
  event.preventDefault()

  const dialog = document.getElementById('create-allowlist-dialog')
  if (!dialog) return

  // Get form values
  const nameInput = dialog.querySelector<HTMLInputElement>('input[name="allowlist[name]"]')
  const descInput = dialog.querySelector<HTMLTextAreaElement>('textarea[name="allowlist[description]"]')

  const name = nameInput?.value?.trim() || ''
  const description = descInput?.value?.trim() || ''

  // Require name
  if (!name) {
    nameInput?.focus()
    return
  }

  const state = getDialogState()
  const serverCount = state.addedServers.length
  const orgNames = state.selectedOrganizations.map(org => org.name)
  const avatarUrl = DEMO_AVATAR_URL
  const userName = DEMO_USER_NAME

  // Add new row to the table
  const table = document.querySelector('[data-test-selector="mcp-allowlists-table"]')
  const emptyState = document.querySelector('[data-test-selector="mcp-allowlists-empty-state"]')

  if (table) {
    const newRow = createAllowlistRow(name, description, serverCount, orgNames, avatarUrl, userName)

    // Insert at the beginning of the Box-body list
    const body = table.querySelector('ul.Box-body')
    if (body) {
      body.prepend(newRow)
    } else {
      // Fallback: insert after the header
      const header = table.querySelector('.Box-header')
      if (header) {
        header.after(newRow)
      }
    }
  } else if (emptyState) {
    // If showing empty state, create the table structure
    const tableContainer = createAllowlistTable(name, description, serverCount, orgNames, avatarUrl, userName)
    emptyState.replaceWith(tableContainer)
  }

  // Reset form
  if (nameInput) nameInput.value = ''
  if (descInput) descInput.value = ''
  resetState()

  // Reset org select button text
  const controller = dialog.querySelector('[data-controller="allowlist-dialog"]')
  if (controller) {
    const orgSelectText = controller.querySelector('[data-allowlist-dialog-target="orgSelectText"]')
    if (orgSelectText) {
      // eslint-disable-next-line i18n-text/no-en
      orgSelectText.textContent = 'All Organizations'
    }
    const orgHiddenInputs = controller.querySelector('[data-allowlist-dialog-target="orgHiddenInputs"]')
    if (orgHiddenInputs) {
      orgHiddenInputs.textContent = ''
    }
  }

  // Show success banner
  showSuccessBanner()

  // Close dialog
  const closeButton = dialog.querySelector('[data-close-dialog-id="create-allowlist-dialog"]') as HTMLElement
  closeButton?.click()
})

// Reset state when dialog is closed
on('click', '[data-close-dialog-id="create-allowlist-dialog"]', function () {
  resetDialogFormState()
})

// Reset state when dialog opens (handles re-opens with stale state)
on('dialog:open', SELECTORS.DIALOG, function () {
  resetDialogFormState()
})

// Reset edit dialogs to main page when opened
on('dialog:open', '[id^="edit-allowlist-dialog-"]', function (event) {
  const dialog = event.currentTarget as Element
  const controller = dialog.querySelector('[data-controller="allowlist-dialog"]')
  if (controller) {
    showPage(controller, 'main')
  }
})

// Handle organization selection from SelectPanel
on('itemActivated', '#allowlist-org-select-panel', function (event) {
  const customEvent = event as CustomEvent<{item: HTMLElement; checked: boolean}>
  const panel = event.currentTarget as Element

  const item = customEvent.detail?.item as HTMLElement | undefined
  if (!item) return

  // Data attributes are on an inner element when using content_arguments
  const contentElement = item.querySelector('[data-value]')
  const orgId = contentElement?.getAttribute('data-value') || ''
  const orgName = contentElement?.getAttribute('data-org-name') || item.textContent?.trim() || ''
  const isSelected = customEvent.detail.checked

  const state = getDialogState()

  if (isSelected) {
    state.addOrganization({id: orgId, name: orgName})
  } else {
    state.removeOrganization(orgId)
  }

  // Update button text
  const controller = getDialogController(panel)
  if (!controller) return

  const orgSelectText = controller.querySelector('[data-allowlist-dialog-target="orgSelectText"]')
  if (orgSelectText) {
    const selectedOrgs = state.selectedOrganizations
    if (selectedOrgs.length === 0) {
      // eslint-disable-next-line i18n-text/no-en
      orgSelectText.textContent = 'All Organizations'
    } else if (selectedOrgs.length === 1) {
      const firstOrg = selectedOrgs[0]
      orgSelectText.textContent = firstOrg ? firstOrg.name : 'All Organizations'
    } else {
      orgSelectText.textContent = `${selectedOrgs.length} organizations`
    }
  }

  // Update hidden inputs
  const orgHiddenInputs = controller.querySelector('[data-allowlist-dialog-target="orgHiddenInputs"]')
  if (orgHiddenInputs) {
    // Clear and rebuild hidden inputs
    orgHiddenInputs.textContent = ''
    for (const org of state.selectedOrganizations) {
      const input = document.createElement('input')
      input.type = 'hidden'
      input.name = 'allowlist[organization_ids][]'
      input.value = org.id
      orgHiddenInputs.appendChild(input)
    }
  }
})
